import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import { getAdsConfig } from "@/lib/ads";
import { useConsent } from "@/lib/consent";

/**
 * AdSlot — renders a Google AdSense unit for Free-tier users only.
 * Config resolves at runtime from the admin dashboard (adsense_client /
 * adsense_slot via /api/config/public), falling back to REACT_APP_ADSENSE_CLIENT
 * and the optional `slot` prop. If nothing is configured, it shows a tasteful
 * "Upgrade to remove ads" nudge so the layout still works.
 */
export default function AdSlot({ slot, format = "auto", className = "", testId = "ad-slot" }) {
  const { user } = useAuth();
  const insRef = useRef(null);
  const [cfg, setCfg] = useState({ client: process.env.REACT_APP_ADSENSE_CLIENT || "", slot: "" });

  // Hide for paid users + during Pro Pass
  const isPaid = user && user.plan && user.plan !== "free";

  useEffect(() => {
    if (isPaid) return;
    let done = false;
    getAdsConfig().then((c) => { if (!done) setCfg(c); });
    return () => { done = true; };
  }, [isPaid]);

  const consented = useConsent() === "accepted";
  const client = consented ? cfg.client : "";
  const adSlot = slot || cfg.slot;

  useEffect(() => {
    if (isPaid || !client || !adSlot) return;
    // Lazy-inject AdSense script once
    if (!document.querySelector("script[data-cognit-adsense]")) {
      const s = document.createElement("script");
      s.async = true;
      s.crossOrigin = "anonymous";
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}`;
      s.setAttribute("data-cognit-adsense", "1");
      document.head.appendChild(s);
    }
    try {
      // eslint-disable-next-line no-undef
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {/* swallow */}
  }, [isPaid, client, adSlot]);

  if (isPaid) return null;

  // Fallback when no AdSense configured — a tasteful upgrade nudge
  if (!client || !adSlot) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={`surface relative overflow-hidden ${className}`}
        data-testid={testId}
      >
        <div
          className="absolute -top-12 -right-12 w-32 h-32 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(124,92,255,0.40), transparent 65%)", filter: "blur(20px)" }}
        />
        <div className="relative px-5 py-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7C5CFF]/30 to-[#5AC8FA]/15 border border-white/10 flex items-center justify-center" style={{ boxShadow: "0 0 18px -6px rgba(124,92,255,0.55)" }}>
            <Sparkles size={16} className="text-[#B8A4FF]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#8E8B99]">Sponsored</div>
            <div className="text-[13.5px] text-white mt-0.5">
              Skip the ads. <span className="text-[#B8A4FF]">Plus is $8/mo</span> — unlocks audio, video, YouTube too.
            </div>
          </div>
          <Link
            to="/app/billing"
            className="px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#7C5CFF] to-[#5AC8FA] text-white text-[12px] font-semibold shrink-0"
            style={{ boxShadow: "0 0 18px -4px rgba(124,92,255,0.6)" }}
            data-testid="adslot-upgrade"
          >
            Upgrade
          </Link>
        </div>
      </motion.div>
    );
  }

  // Real AdSense render
  return (
    <div className={`relative ${className}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#8E8B99]">Sponsored</span>
        <Link
          to="/app/billing"
          title="Upgrade to Plus or Pro to remove all ads"
          className="text-[10px] font-medium text-[#8E8B99] hover:text-[#B8A4FF] transition-colors inline-flex items-center gap-1"
          data-testid="stop-seeing-ads"
        >
          <X size={11} strokeWidth={2.4} /> Stop seeing ads
        </Link>
      </div>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block", minHeight: 100 }}
        data-ad-client={client}
        data-ad-slot={adSlot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}
